import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface UploadSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
  cvId?: string;
}

const UploadSuccessModal: React.FC<UploadSuccessModalProps> = ({
  isOpen,
  onClose,
  email,
  cvId
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('inspire');

  const handleSetupRealTalk = () => {
    onClose();
    // Real Talk now lives on the Passport tab (Status & Real-Talk page), not the
    // legacy Profile section (which is gated off). Deep-link straight to it.
    navigate('/?tab=passport&pp=realtalk');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors z-10"
            >
              <X size={20} className="text-slate-400" />
            </button>

            {/* Header with animated icon - compact */}
            <div className="bg-gradient-to-br from-pink-500 via-orange-400 to-yellow-400 px-4 sm:px-6 py-4 sm:py-5 text-center">
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', damping: 15 }}
                  className="relative"
                >
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-spin" style={{ animationDuration: '3s' }} />
                </motion.div>
                <div className="text-left">
                  <h2 className="text-base sm:text-lg font-bold text-white">
                    {t('uploadModal.title', 'Processing your CV...')}
                  </h2>
                  <p className="text-white/80 text-xs">
                    {t('uploadModal.subtitle', 'This can take up to 15 minutes')}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              {/* Email notification */}
              <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-pink-50 rounded-xl mb-4 sm:mb-6">
                <div className="text-xl sm:text-2xl">📧</div>
                <p className="font-medium text-slate-800 text-xs sm:text-sm">
                  {t('uploadModal.emailNotify', "We'll notify you via email when your CV is ready.")}
                </p>
              </div>

              {/* Divider with "Thanks for inspiring" */}
              <div className="relative my-4 sm:my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-pink-200"></div>
                </div>
                <div className="relative flex justify-center text-xs sm:text-sm">
                  <span className="px-3 bg-white text-pink-600 font-medium">
                    ✨ {t('uploadModal.thanksForInspiring', 'Thanks for inspiring!')}
                  </span>
                </div>
              </div>

              {/* Real Talk nudge */}
              <div className="bg-gradient-to-br from-pink-50 to-yellow-50 rounded-xl p-3 sm:p-4 border border-pink-200">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="text-xl sm:text-2xl">🗣️</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 mb-1 text-sm sm:text-base">
                      {t('uploadModal.realTalkTitle', 'Help us build an authentic community')}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm mb-2">
                      {t('uploadModal.realTalkText', 'Add your "Real Talk" - a short sentence that shows the real you. What makes you unique?')}
                    </p>
                    <p className="text-slate-500 text-xs mb-3">
                      🔒 {t('uploadModal.explorersOnly', 'Only visible to explorers')}
                    </p>
                    <button
                      onClick={handleSetupRealTalk}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-600 to-orange-500 hover:from-pink-700 hover:to-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('uploadModal.realTalkButton', 'Set up Real Talk')}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              <button
                onClick={onClose}
                className="w-full py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-xl transition-colors text-sm font-medium"
              >
                {t('uploadModal.gotIt', 'Got it, thanks!')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UploadSuccessModal;

